import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus, GripVertical, Trash2, Image as ImageIcon, Type, ShoppingBag,
  FolderTree, Video, HelpCircle, Save, Monitor, BookOpen, Upload, X, Check, Images, LayoutPanelLeft, Tag, Clock, MapPin, Columns
} from 'lucide-react';

const SECTION_TYPES = [
  { type: 'hero_banner', label: 'Banniere Hero', icon: ImageIcon, desc: 'Image pleine largeur avec titre' },
  { type: 'image_text', label: 'Image + Texte', icon: LayoutPanelLeft, desc: 'Image et texte cote a cote' },
  { type: 'image_slider', label: 'Images / Slider', icon: Images, desc: 'Une ou plusieurs images en slider' },
  { type: 'promotion', label: 'Promotion / Offre', icon: Tag, desc: 'Offre speciale avec compte a rebours' },
  { type: 'opening_hours', label: 'Horaires', icon: Clock, desc: 'Horaires d\'ouverture avec indicateur' },
  { type: 'google_map', label: 'Carte Google Maps', icon: MapPin, desc: 'Localisation interactive' },
  { type: 'before_after', label: 'Avant / Apres', icon: Columns, desc: 'Slider comparatif avant/apres' },
  { type: 'text', label: 'Titre & Texte', icon: Type, desc: 'Bloc de texte personnalisable' },
  { type: 'products', label: 'Produits', icon: ShoppingBag, desc: 'Selection de produits' },
  { type: 'categories', label: 'Categories', icon: FolderTree, desc: 'Selection de categories' },
  { type: 'video', label: 'Video', icon: Video, desc: 'Video YouTube integree' },
  { type: 'faq', label: 'FAQ', icon: HelpCircle, desc: 'Questions / Reponses' },
];

const SortableSection = ({ section, onUpdate, onDelete, onImageUpload, onSliderImageUpload, onSliderImageDelete, products, categories }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 50 : 'auto' };
  const typeMeta = SECTION_TYPES.find(t => t.type === section.type) || SECTION_TYPES[0];
  const Icon = typeMeta.icon;

  return (
    <div ref={setNodeRef} style={style} data-testid={`section-${section.id}`}>
      <Card className="border">
        <CardHeader className="py-3 px-4">
          <div className="flex items-center gap-3">
            <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-muted-foreground hover:text-foreground" data-testid={`drag-handle-${section.id}`}>
              <GripVertical className="w-5 h-5" />
            </button>
            <Icon className="w-5 h-5 text-primary" />
            <CardTitle className="text-sm flex-1">{typeMeta.label}</CardTitle>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onDelete(section.id)} data-testid={`delete-section-${section.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0">
          <SectionEditor section={section} onUpdate={onUpdate} onImageUpload={onImageUpload} onSliderImageUpload={onSliderImageUpload} onSliderImageDelete={onSliderImageDelete} products={products} categories={categories} />
        </CardContent>
      </Card>
    </div>
  );
};

const SectionEditor = ({ section, onUpdate, onImageUpload, onSliderImageUpload, onSliderImageDelete, products, categories }) => {
  const c = section.content || {};

  const updateContent = (key, value) => {
    onUpdate(section.id, { ...c, [key]: value });
  };

  switch (section.type) {
    case 'image_slider': {
      const imageList = c.images || [];
      return (
        <div className="space-y-3" data-testid={`slider-editor-${section.id}`}>
          <Label className="text-xs">Images ({imageList.length})</Label>
          {imageList.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {imageList.map(function(imgUrl, idx) {
                return (
                  <div key={idx} className="relative group">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${imgUrl}`} alt="" className="w-full h-24 object-cover rounded-lg border" />
                    <button
                      onClick={function() { onSliderImageDelete(section.id, idx); }}
                      className="absolute top-1 right-1 bg-black/60 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-slider-img-${idx}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <span className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">{idx + 1}</span>
                  </div>
                );
              })}
            </div>
          )}
          <div>
            <input type="file" accept="image/*" className="hidden" id={`slider-img-${section.id}`} onChange={function(e) { onSliderImageUpload(section.id, e.target.files[0]); e.target.value = ''; }} />
            <Button type="button" variant="outline" size="sm" onClick={function() { document.getElementById(`slider-img-${section.id}`).click(); }} data-testid={`add-slider-image-${section.id}`}>
              <Upload className="w-4 h-4 mr-2" /> Ajouter une image
            </Button>
          </div>
          {imageList.length > 1 && (
            <p className="text-xs text-muted-foreground">{imageList.length} images = slider automatique avec navigation</p>
          )}
          {imageList.length === 1 && (
            <p className="text-xs text-muted-foreground">1 image = affichage simple pleine largeur</p>
          )}
        </div>
      );
    }
    case 'image_text':
      return (
        <div className="space-y-3" data-testid={`image-text-editor-${section.id}`}>
          <div>
            <Label className="text-xs">Image</Label>
            {c.imageUrl ? (
              <div className="relative mt-1">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${c.imageUrl}`} alt="" className="w-full h-32 object-cover rounded-lg" />
                <button onClick={function() { updateContent('imageUrl', null); }} className="absolute top-1 right-1 bg-black/60 text-white p-1 rounded-full"><X className="w-3 h-3" /></button>
              </div>
            ) : (
              <div className="mt-1">
                <input type="file" accept="image/*" className="hidden" id={`imgtext-${section.id}`} onChange={function(e) { onImageUpload(section.id, e.target.files[0]); }} />
                <Button type="button" variant="outline" size="sm" onClick={function() { document.getElementById(`imgtext-${section.id}`).click(); }}>
                  <Upload className="w-4 h-4 mr-2" /> Choisir une image
                </Button>
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Position de l'image (PC)</Label>
            <div className="flex gap-2 mt-1">
              <Button type="button" variant={c.imagePosition === 'right' ? 'default' : 'outline'} size="sm" onClick={function() { updateContent('imagePosition', 'right'); }} data-testid="img-pos-right">Image a droite</Button>
              <Button type="button" variant={c.imagePosition === 'left' ? 'default' : 'outline'} size="sm" onClick={function() { updateContent('imagePosition', 'left'); }} data-testid="img-pos-left">Image a gauche</Button>
            </div>
          </div>
          <div>
            <Label className="text-xs">Titre</Label>
            <Input value={c.title || ''} onChange={function(e) { updateContent('title', e.target.value); }} placeholder="Notre collection" data-testid={`section-title-${section.id}`} />
          </div>
          <div>
            <Label className="text-xs">Sous-titre</Label>
            <Input value={c.subtitle || ''} onChange={function(e) { updateContent('subtitle', e.target.value); }} placeholder="Un sous-titre accrocheur" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <textarea className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm" value={c.description || ''} onChange={function(e) { updateContent('description', e.target.value); }} placeholder="Decrivez votre offre..." />
          </div>
          <div className="border-t pt-3 space-y-3">
            <Label className="text-xs font-semibold">Bouton (optionnel)</Label>
            <div>
              <Label className="text-xs">Texte du bouton</Label>
              <Input value={c.buttonText || ''} onChange={function(e) { updateContent('buttonText', e.target.value); }} placeholder="Voir plus" />
            </div>
            <div>
              <Label className="text-xs">Lien du bouton</Label>
              <Input value={c.buttonUrl || ''} onChange={function(e) { updateContent('buttonUrl', e.target.value); }} placeholder="https://..." />
            </div>
            <div>
              <Label className="text-xs">Couleur du bouton</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={c.buttonColor || '#000000'} onChange={function(e) { updateContent('buttonColor', e.target.value); }} className="w-8 h-8 rounded cursor-pointer border shrink-0" />
                <Input value={c.buttonColor || '#000000'} onChange={function(e) { updateContent('buttonColor', e.target.value); }} className="h-8 text-xs" />
              </div>
            </div>
          </div>
        </div>
      );
    case 'hero_banner':
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Titre</Label>
            <Input value={c.title || ''} onChange={e => updateContent('title', e.target.value)} placeholder="Bienvenue dans notre boutique" data-testid={`section-title-${section.id}`} />
          </div>
          <div>
            <Label className="text-xs">Sous-titre</Label>
            <Input value={c.subtitle || ''} onChange={e => updateContent('subtitle', e.target.value)} placeholder="Decouvrez nos nouveautes" />
          </div>
          <div>
            <Label className="text-xs">Image de banniere</Label>
            {c.imageUrl ? (
              <div className="relative mt-1">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${c.imageUrl}`} alt="" className="w-full h-32 object-cover rounded-lg" />
                <button onClick={() => updateContent('imageUrl', null)} className="absolute top-1 right-1 bg-black/60 text-white p-1 rounded-full"><X className="w-3 h-3" /></button>
              </div>
            ) : (
              <div className="mt-1">
                <input type="file" accept="image/*" className="hidden" id={`img-${section.id}`} onChange={e => onImageUpload(section.id, e.target.files[0])} />
                <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById(`img-${section.id}`).click()}>
                  <Upload className="w-4 h-4 mr-2" /> Choisir une image
                </Button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Label className="text-xs">Couleur de fond (sans image)</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={c.bgColor || '#F59E0B'} onChange={e => updateContent('bgColor', e.target.value)} className="w-8 h-8 rounded cursor-pointer border shrink-0" />
                <Input value={c.bgColor || '#F59E0B'} onChange={e => updateContent('bgColor', e.target.value)} className="h-8 text-xs" />
              </div>
            </div>
          </div>
          <div className="border-t pt-3 space-y-3">
            <Label className="text-xs font-semibold">Bouton (optionnel)</Label>
            <div>
              <Label className="text-xs">Texte du bouton</Label>
              <Input value={c.buttonText || ''} onChange={e => updateContent('buttonText', e.target.value)} placeholder="Voir les produits" />
            </div>
            <div>
              <Label className="text-xs">Lien du bouton</Label>
              <Input value={c.buttonUrl || ''} onChange={e => updateContent('buttonUrl', e.target.value)} placeholder="https://..." />
            </div>
            <div>
              <Label className="text-xs">Couleur du bouton</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={c.buttonColor || '#FFFFFF'} onChange={e => updateContent('buttonColor', e.target.value)} className="w-8 h-8 rounded cursor-pointer border shrink-0" />
                <Input value={c.buttonColor || '#FFFFFF'} onChange={e => updateContent('buttonColor', e.target.value)} className="h-8 text-xs" />
              </div>
            </div>
          </div>
        </div>
      );
    case 'text':
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Titre</Label>
            <Input value={c.title || ''} onChange={e => updateContent('title', e.target.value)} placeholder="Notre histoire" data-testid={`section-title-${section.id}`} />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea value={c.description || ''} onChange={e => updateContent('description', e.target.value)} placeholder="Racontez votre histoire..." rows={3} />
          </div>
        </div>
      );
    case 'products':
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Titre de la section</Label>
            <Input value={c.title || ''} onChange={e => updateContent('title', e.target.value)} placeholder="Nos coups de coeur" data-testid={`section-title-${section.id}`} />
          </div>
          <div>
            <Label className="text-xs mb-2 block">Produits a afficher</Label>
            <div className="max-h-48 overflow-y-auto space-y-1 border rounded-lg p-2">
              {products.map(p => {
                const selected = (c.productIds || []).includes(p.id);
                return (
                  <label key={p.id} className={`flex items-center gap-2 p-1.5 rounded cursor-pointer text-sm transition-colors ${selected ? 'bg-primary/10' : 'hover:bg-muted'}`}>
                    <input type="checkbox" checked={selected} onChange={() => {
                      const ids = c.productIds || [];
                      updateContent('productIds', selected ? ids.filter(id => id !== p.id) : [...ids, p.id]);
                    }} className="rounded" />
                    <span className="truncate">{p.name}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-1">{(c.productIds || []).length} produit(s) selectionne(s)</p>
          </div>
        </div>
      );
    case 'categories':
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Titre de la section</Label>
            <Input value={c.title || ''} onChange={e => updateContent('title', e.target.value)} placeholder="Nos categories" data-testid={`section-title-${section.id}`} />
          </div>
          <div>
            <Label className="text-xs mb-2 block">Categories a afficher</Label>
            <div className="space-y-1 border rounded-lg p-2">
              {categories.map(cat => {
                const selected = (c.categoryIds || []).includes(cat.id);
                return (
                  <label key={cat.id} className={`flex items-center gap-2 p-1.5 rounded cursor-pointer text-sm transition-colors ${selected ? 'bg-primary/10' : 'hover:bg-muted'}`}>
                    <input type="checkbox" checked={selected} onChange={() => {
                      const ids = c.categoryIds || [];
                      updateContent('categoryIds', selected ? ids.filter(id => id !== cat.id) : [...ids, cat.id]);
                    }} className="rounded" />
                    <span>{cat.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      );
    case 'video':
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Titre (optionnel)</Label>
            <Input value={c.title || ''} onChange={e => updateContent('title', e.target.value)} placeholder="Notre video" data-testid={`section-title-${section.id}`} />
          </div>
          <div>
            <Label className="text-xs">URL YouTube</Label>
            <Input value={c.videoUrl || ''} onChange={e => updateContent('videoUrl', e.target.value)} placeholder="https://www.youtube.com/watch?v=..." />
            <p className="text-xs text-muted-foreground mt-1">Collez le lien de la video YouTube</p>
          </div>
        </div>
      );
    case 'faq':
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Titre</Label>
            <Input value={c.title || ''} onChange={e => updateContent('title', e.target.value)} placeholder="Questions frequentes" data-testid={`section-title-${section.id}`} />
          </div>
          <div>
            <Label className="text-xs mb-2 block">Questions / Reponses</Label>
            {(c.faqItems || []).map((item, i) => (
              <div key={i} className="border rounded-lg p-3 mb-2 space-y-2">
                <div className="flex items-center gap-2">
                  <Input value={item.question || ''} onChange={e => {
                    const items = [...(c.faqItems || [])];
                    items[i] = { ...items[i], question: e.target.value };
                    updateContent('faqItems', items);
                  }} placeholder="Question" className="text-sm" />
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-destructive" onClick={() => {
                    const items = (c.faqItems || []).filter((_, j) => j !== i);
                    updateContent('faqItems', items);
                  }}><X className="w-3 h-3" /></Button>
                </div>
                <Textarea value={item.answer || ''} onChange={e => {
                  const items = [...(c.faqItems || [])];
                  items[i] = { ...items[i], answer: e.target.value };
                  updateContent('faqItems', items);
                }} placeholder="Reponse" rows={2} className="text-sm" />
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => updateContent('faqItems', [...(c.faqItems || []), { question: '', answer: '' }])}>
              <Plus className="w-3 h-3 mr-1" /> Ajouter une question
            </Button>
          </div>
        </div>
      );
    case 'promotion':
      return (
        <div className="space-y-3" data-testid={`promo-editor-${section.id}`}>
          <div>
            <Label className="text-xs">Titre de l'offre</Label>
            <Input value={c.title || ''} onChange={function(e) { updateContent('title', e.target.value); }} placeholder="Soldes d'ete -30%" data-testid={`section-title-${section.id}`} />
          </div>
          <div>
            <Label className="text-xs">Sous-titre / Code promo</Label>
            <Input value={c.subtitle || ''} onChange={function(e) { updateContent('subtitle', e.target.value); }} placeholder="Code: SUMMER30" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <textarea className="w-full min-h-[60px] rounded-md border border-input bg-background px-3 py-2 text-sm" value={c.description || ''} onChange={function(e) { updateContent('description', e.target.value); }} placeholder="Profitez de -30% sur toute la collection..." />
          </div>
          <div>
            <Label className="text-xs">Reduction a afficher</Label>
            <Input value={c.discountText || ''} onChange={function(e) { updateContent('discountText', e.target.value); }} placeholder="-30%" />
          </div>
          <div>
            <Label className="text-xs">Date de fin (compte a rebours)</Label>
            <Input type="datetime-local" value={c.endDate || ''} onChange={function(e) { updateContent('endDate', e.target.value); }} data-testid="promo-end-date" />
            <p className="text-xs text-muted-foreground mt-1">Un compte a rebours s'affichera jusqu'a cette date</p>
          </div>
          <div>
            <Label className="text-xs">Image (optionnel)</Label>
            {c.imageUrl ? (
              <div className="relative mt-1">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${c.imageUrl}`} alt="" className="w-full h-32 object-cover rounded-lg" />
                <button onClick={function() { updateContent('imageUrl', null); }} className="absolute top-1 right-1 bg-black/60 text-white p-1 rounded-full"><X className="w-3 h-3" /></button>
              </div>
            ) : (
              <div className="mt-1">
                <input type="file" accept="image/*" className="hidden" id={`promo-img-${section.id}`} onChange={function(e) { onImageUpload(section.id, e.target.files[0]); }} />
                <Button type="button" variant="outline" size="sm" onClick={function() { document.getElementById(`promo-img-${section.id}`).click(); }}>
                  <Upload className="w-4 h-4 mr-2" /> Choisir une image
                </Button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Couleur de fond</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={c.bgColor || '#DC2626'} onChange={function(e) { updateContent('bgColor', e.target.value); }} className="w-8 h-8 rounded cursor-pointer border shrink-0" />
                <Input value={c.bgColor || '#DC2626'} onChange={function(e) { updateContent('bgColor', e.target.value); }} className="h-8 text-xs" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Couleur du texte</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={c.textColor || '#FFFFFF'} onChange={function(e) { updateContent('textColor', e.target.value); }} className="w-8 h-8 rounded cursor-pointer border shrink-0" />
                <Input value={c.textColor || '#FFFFFF'} onChange={function(e) { updateContent('textColor', e.target.value); }} className="h-8 text-xs" />
              </div>
            </div>
          </div>
          <div className="border-t pt-3 space-y-3">
            <Label className="text-xs font-semibold">Bouton (optionnel)</Label>
            <div>
              <Label className="text-xs">Texte du bouton</Label>
              <Input value={c.buttonText || ''} onChange={function(e) { updateContent('buttonText', e.target.value); }} placeholder="Voir les offres" />
            </div>
            <div>
              <Label className="text-xs">Lien du bouton</Label>
              <Input value={c.buttonUrl || ''} onChange={function(e) { updateContent('buttonUrl', e.target.value); }} placeholder="https://..." />
            </div>
          </div>
        </div>
      );
    case 'opening_hours':
      return <OpeningHoursEditor c={c} updateContent={updateContent} sectionId={section.id} />;
    case 'google_map':
      return <GoogleMapEditor c={c} updateContent={updateContent} sectionId={section.id} />;
    case 'before_after':
      return <BeforeAfterEditor c={c} updateContent={updateContent} onImageUpload={onImageUpload} sectionId={section.id} />;
    default:
      return null;
  }
};

const DAYS_OF_WEEK = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const DEFAULT_SCHEDULE = DAYS_OF_WEEK.map(function(d) { return { day: d, openTime: '09:00', closeTime: '18:00', isClosed: 'false' }; });

const OpeningHoursEditor = ({ c, updateContent, sectionId }) => {
  const schedule = c.schedule && c.schedule.length > 0 ? c.schedule : DEFAULT_SCHEDULE;
  const updateDay = function(idx, field, value) {
    const updated = schedule.map(function(item, i) { return i === idx ? { ...item, [field]: value } : item; });
    updateContent('schedule', updated);
  };
  return (
    <div className="space-y-3" data-testid={"hours-editor-" + sectionId}>
      <div>
        <Label className="text-xs">Titre</Label>
        <Input value={c.title || ''} onChange={function(e) { updateContent('title', e.target.value); }} placeholder="Nos horaires d'ouverture" />
      </div>
      <Label className="text-xs font-semibold">Horaires par jour</Label>
      {schedule.map(function(item, idx) {
        return (
          <div key={idx} className="flex items-center gap-2 border rounded-lg p-2">
            <span className="w-20 text-sm font-medium shrink-0">{item.day}</span>
            <label className="flex items-center gap-1 shrink-0">
              <input type="checkbox" checked={item.isClosed === 'true'} onChange={function(e) { updateDay(idx, 'isClosed', e.target.checked ? 'true' : 'false'); }} />
              <span className="text-xs text-muted-foreground">Ferme</span>
            </label>
            {item.isClosed !== 'true' && (
              <>
                <Input type="time" value={item.openTime || '09:00'} onChange={function(e) { updateDay(idx, 'openTime', e.target.value); }} className="h-8 text-xs w-24" />
                <span className="text-xs">a</span>
                <Input type="time" value={item.closeTime || '18:00'} onChange={function(e) { updateDay(idx, 'closeTime', e.target.value); }} className="h-8 text-xs w-24" />
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

const GoogleMapEditor = ({ c, updateContent, sectionId }) => {
  return (
    <div className="space-y-3" data-testid={"map-editor-" + sectionId}>
      <div>
        <Label className="text-xs">Titre</Label>
        <Input value={c.title || ''} onChange={function(e) { updateContent('title', e.target.value); }} placeholder="Nous trouver" />
      </div>
      <div>
        <Label className="text-xs">Adresse ou recherche Google Maps</Label>
        <Input value={c.mapQuery || ''} onChange={function(e) { updateContent('mapQuery', e.target.value); }} placeholder="33 Boulevard Mahatma Gandhi, 97490 Saint-Denis" />
        <p className="text-xs text-muted-foreground mt-1">L'adresse de votre boutique sera utilisee si ce champ est vide</p>
      </div>
      <div>
        <Label className="text-xs">Description (optionnel)</Label>
        <textarea className="w-full min-h-[40px] rounded-md border border-input bg-background px-3 py-2 text-sm" value={c.description || ''} onChange={function(e) { updateContent('description', e.target.value); }} placeholder="Venez nous rendre visite !" />
      </div>
    </div>
  );
};

const BeforeAfterEditor = ({ c, updateContent, onImageUpload, sectionId }) => {
  return (
    <div className="space-y-3" data-testid={"ba-editor-" + sectionId}>
      <div>
        <Label className="text-xs">Titre</Label>
        <Input value={c.title || ''} onChange={function(e) { updateContent('title', e.target.value); }} placeholder="Avant / Apres" />
      </div>
      <div>
        <Label className="text-xs">Sous-titre</Label>
        <Input value={c.subtitle || ''} onChange={function(e) { updateContent('subtitle', e.target.value); }} placeholder="Decouvrez la transformation" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Image AVANT</Label>
          {c.beforeImage ? (
            <div className="relative mt-1">
              <img src={process.env.REACT_APP_BACKEND_URL + c.beforeImage} alt="Avant" className="w-full h-24 object-cover rounded-lg" />
              <button onClick={function() { updateContent('beforeImage', null); }} className="absolute top-1 right-1 bg-black/60 text-white p-1 rounded-full"><X className="w-3 h-3" /></button>
            </div>
          ) : (
            <div className="mt-1">
              <input type="file" accept="image/*" className="hidden" id={"ba-before-" + sectionId} onChange={function(e) {
                var file = e.target.files[0]; if (!file) return;
                var formData = new FormData(); formData.append('file', file);
                onImageUpload(sectionId, file, 'beforeImage');
              }} />
              <Button type="button" variant="outline" size="sm" onClick={function() { document.getElementById("ba-before-" + sectionId).click(); }}>
                <Upload className="w-3 h-3 mr-1" /> Avant
              </Button>
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs">Image APRES</Label>
          {c.afterImage ? (
            <div className="relative mt-1">
              <img src={process.env.REACT_APP_BACKEND_URL + c.afterImage} alt="Apres" className="w-full h-24 object-cover rounded-lg" />
              <button onClick={function() { updateContent('afterImage', null); }} className="absolute top-1 right-1 bg-black/60 text-white p-1 rounded-full"><X className="w-3 h-3" /></button>
            </div>
          ) : (
            <div className="mt-1">
              <input type="file" accept="image/*" className="hidden" id={"ba-after-" + sectionId} onChange={function(e) {
                var file = e.target.files[0]; if (!file) return;
                onImageUpload(sectionId, file, 'afterImage');
              }} />
              <Button type="button" variant="outline" size="sm" onClick={function() { document.getElementById("ba-after-" + sectionId).click(); }}>
                <Upload className="w-3 h-3 mr-1" /> Apres
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ShopPageBuilder = () => {
  const { shopId } = useParams();
  const { api, user } = useAuth();
  const [shop, setShop] = useState(null);
  const [sections, setSections] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [displayMode, setDisplayMode] = useState('catalog');

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const fetchData = useCallback(async () => {
    try {
      const [shopRes, sectionsRes, productsRes, categoriesRes] = await Promise.all([
        api.get(`/shops/${shopId}`),
        api.get(`/shops/${shopId}/homepage-sections`),
        api.get(`/shops/${shopId}/products`),
        api.get(`/shops/${shopId}/categories`)
      ]);
      setShop(shopRes.data);
      setSections(sectionsRes.data);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setDisplayMode(shopRes.data.displayMode || 'catalog');
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [shopId, api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDisplayModeChange = async (mode) => {
    setDisplayMode(mode);
    try {
      await api.put(`/shops/${shopId}`, { displayMode: mode });
      toast.success(mode === 'website' ? 'Mode Site Web active' : 'Mode Catalogue active');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const addSection = async (type) => {
    setShowAddMenu(false);
    try {
      const res = await api.post(`/shops/${shopId}/homepage-sections`, { type, content: {} });
      setSections(prev => [...prev, res.data]);
      toast.success('Section ajoutee');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const updateSectionContent = (sectionId, content) => {
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, content } : s));
  };

  const deleteSection = async (sectionId) => {
    try {
      await api.delete(`/shops/${shopId}/homepage-sections/${sectionId}`);
      setSections(prev => prev.filter(s => s.id !== sectionId));
      toast.success('Section supprimee');
    } catch (error) {
      toast.error('Erreur de suppression');
    }
  };

  const handleImageUpload = async (sectionId, file, fieldName) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    if (fieldName) fd.append('field_name', fieldName);
    try {
      const res = await api.post(`/shops/${shopId}/homepage-sections/${sectionId}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSections(prev => prev.map(s => s.id === sectionId ? { ...s, content: res.data.content } : s));
      toast.success('Image uploadee');
    } catch (error) {
      toast.error('Erreur upload');
    }
  };

  const handleSliderImageUpload = async (sectionId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post(`/shops/${shopId}/homepage-sections/${sectionId}/slider-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSections(prev => prev.map(s => s.id === sectionId ? { ...s, content: res.data.content } : s));
      toast.success('Image ajoutee au slider');
    } catch (error) {
      toast.error('Erreur upload');
    }
  };

  const handleSliderImageDelete = async (sectionId, imageIndex) => {
    try {
      const res = await api.delete(`/shops/${shopId}/homepage-sections/${sectionId}/slider-image/${imageIndex}`);
      setSections(prev => prev.map(s => s.id === sectionId ? { ...s, content: res.data.content } : s));
      toast.success('Image supprimee');
    } catch (error) {
      toast.error('Erreur suppression');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sections.findIndex(s => s.id === active.id);
    const newIndex = sections.findIndex(s => s.id === over.id);
    const newOrder = arrayMove(sections, oldIndex, newIndex);
    setSections(newOrder);
    try {
      await api.post(`/shops/${shopId}/homepage-sections/reorder`, newOrder.map(s => s.id));
    } catch (error) {
      toast.error('Erreur de reordonnancement');
    }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      for (const section of sections) {
        await api.put(`/shops/${shopId}/homepage-sections/${section.id}`, { content: section.content });
      }
      toast.success('Toutes les sections sauvegardees !');
    } catch (error) {
      toast.error('Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  const isPremium = user?.plan === 'PREMIUM';

  return (
    <div className="space-y-6 max-w-2xl" data-testid="page-builder">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">Page d'accueil</h1>
        <p className="text-muted-foreground mt-1">Personnalisez la page publique de {shop?.name}</p>
      </div>

      {/* Mode selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Mode d'affichage</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => handleDisplayModeChange('catalog')}
              className={`p-4 rounded-xl border-2 transition-all text-left ${displayMode === 'catalog' ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary/40'}`}
              data-testid="mode-catalog"
            >
              <BookOpen className="w-6 h-6 mb-2 text-primary" />
              <p className="font-semibold text-sm">Catalogue</p>
              <p className="text-xs text-muted-foreground mt-1">Affichage classique par categories</p>
              {displayMode === 'catalog' && <Check className="w-4 h-4 text-primary mt-2" />}
            </button>
            <button
              onClick={() => isPremium ? handleDisplayModeChange('website') : toast.error('Reservee au plan PREMIUM')}
              className={`p-4 rounded-xl border-2 transition-all text-left ${displayMode === 'website' ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary/40'} ${!isPremium ? 'opacity-50' : ''}`}
              data-testid="mode-website"
            >
              <Monitor className="w-6 h-6 mb-2 text-primary" />
              <p className="font-semibold text-sm">Site Web</p>
              <p className="text-xs text-muted-foreground mt-1">Page d'accueil personnalisable</p>
              {displayMode === 'website' && <Check className="w-4 h-4 text-primary mt-2" />}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Page builder (only in website mode) */}
      {displayMode === 'website' && (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{sections.length} section(s)</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowAddMenu(!showAddMenu)} data-testid="add-section-btn">
                <Plus className="w-4 h-4 mr-1" /> Ajouter
              </Button>
              <Button size="sm" onClick={saveAll} disabled={saving} data-testid="save-sections-btn">
                <Save className="w-4 h-4 mr-1" /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </div>
          </div>

          {/* Add section menu */}
          {showAddMenu && (
            <Card>
              <CardContent className="p-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {SECTION_TYPES.map(st => (
                    <button
                      key={st.type}
                      onClick={() => addSection(st.type)}
                      className="p-3 rounded-lg border hover:bg-muted transition-colors text-left"
                      data-testid={`add-${st.type}`}
                    >
                      <st.icon className="w-5 h-5 text-primary mb-1" />
                      <p className="text-sm font-medium">{st.label}</p>
                      <p className="text-xs text-muted-foreground">{st.desc}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sections list with drag & drop */}
          {sections.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Monitor className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Aucune section. Cliquez sur "Ajouter" pour commencer.</p>
              </CardContent>
            </Card>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-3">
                  {sections.map(section => (
                    <SortableSection
                      key={section.id}
                      section={section}
                      onUpdate={updateSectionContent}
                      onDelete={deleteSection}
                      onImageUpload={handleImageUpload}
                      onSliderImageUpload={handleSliderImageUpload}
                      onSliderImageDelete={handleSliderImageDelete}
                      products={products}
                      categories={categories}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </>
      )}

      {displayMode === 'catalog' && (
        <Card>
          <CardContent className="py-8 text-center">
            <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="font-medium">Mode Catalogue actif</p>
            <p className="text-sm text-muted-foreground mt-1">Votre page publique affiche le catalogue classique par categories.</p>
            <p className="text-sm text-muted-foreground">Passez en mode "Site Web" pour personnaliser la page d'accueil.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ShopPageBuilder;
